import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
  Drawer,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';

const companiesSeed = [
  {
    id: 'CMP-001',
    companyName: 'Acme Corporation',
    industry: 'Technology',
    contactPerson: 'Sarah Johnson',
    phone: '+1 202-555-0121',
    email: 'sarah.johnson@acme.com',
    activeOpportunities: 4,
    revenue: 1250000,
    website: 'www.acme.com',
    size: 'Enterprise',
    location: 'New York, USA',
    notes: 'Strategic partner account with multi-year growth potential.',
    contacts: [
      { name: 'Sarah Johnson', role: 'Head of Procurement', email: 'sarah.johnson@acme.com' },
      { name: 'Michael Reed', role: 'IT Director', email: 'michael.reed@acme.com' },
    ],
    opportunities: [
      { name: 'CRM Rollout Q3', stage: 'Negotiation', value: 180000 },
      { name: 'Support Renewal', stage: 'Proposal', value: 95000 },
    ],
    quotes: [
      { number: 'QT-2026-041', status: 'Sent', amount: 125000 },
      { number: 'QT-2026-033', status: 'Accepted', amount: 84000 },
    ],
    activities: [
      { date: '2026-06-05', text: 'Quarterly business review completed.' },
      { date: '2026-06-08', text: 'Shared updated pricing proposal.' },
    ],
    files: ['MSA_Acme_2026.pdf', 'QBR_June_Deck.pptx'],
  },
  {
    id: 'CMP-002',
    companyName: 'BrightPath Logistics',
    industry: 'Logistics',
    contactPerson: 'Daniel Moore',
    phone: '+1 202-555-0198',
    email: 'daniel.moore@brightpath.com',
    activeOpportunities: 2,
    revenue: 640000,
    website: 'www.brightpath.com',
    size: 'Mid-Market',
    location: 'Dallas, USA',
    notes: 'Interested in workflow automation for warehouse operations.',
    contacts: [{ name: 'Daniel Moore', role: 'Operations Manager', email: 'daniel.moore@brightpath.com' }],
    opportunities: [{ name: 'Warehouse Automation Suite', stage: 'Qualified', value: 220000 }],
    quotes: [{ number: 'QT-2026-052', status: 'Draft', amount: 61000 }],
    activities: [{ date: '2026-06-04', text: 'Discovery call with operations team.' }],
    files: ['RequirementNotes_BrightPath.docx'],
  },
  {
    id: 'CMP-003',
    companyName: 'Nova Health Systems',
    industry: 'Healthcare',
    contactPerson: 'Emily Carter',
    phone: '+1 202-555-0170',
    email: 'emily.carter@novahealth.com',
    activeOpportunities: 3,
    revenue: 910000,
    website: 'www.novahealth.com',
    size: 'Enterprise',
    location: 'Boston, USA',
    notes: 'Compliance-heavy account, needs HIPAA-ready workflow tracking.',
    contacts: [{ name: 'Emily Carter', role: 'Chief Transformation Officer', email: 'emily.carter@novahealth.com' }],
    opportunities: [{ name: 'Compliance Workflow Program', stage: 'Proposal', value: 300000 }],
    quotes: [{ number: 'QT-2026-047', status: 'Sent', amount: 98000 }],
    activities: [{ date: '2026-06-02', text: 'Compliance workshop session completed.' }],
    files: ['HIPAA_Workflow_Checklist.xlsx'],
  },
  {
    id: 'CMP-004',
    companyName: 'GreenCore Energy',
    industry: 'Energy',
    contactPerson: 'Ryan Patel',
    phone: '+1 202-555-0133',
    email: 'ryan.patel@greencore.com',
    activeOpportunities: 1,
    revenue: 420000,
    website: 'www.greencoreenergy.com',
    size: 'SMB',
    location: 'Austin, USA',
    notes: 'Fast-moving account, decision expected in 2 weeks.',
    contacts: [{ name: 'Ryan Patel', role: 'Procurement Lead', email: 'ryan.patel@greencore.com' }],
    opportunities: [{ name: 'Field Ops Dashboard', stage: 'Discovery', value: 70000 }],
    quotes: [{ number: 'QT-2026-056', status: 'Draft', amount: 38000 }],
    activities: [{ date: '2026-06-07', text: 'Sent implementation timeline.' }],
    files: ['GreenCore_ProjectTimeline.pdf'],
  },
];

const CompanyProfileDrawer = ({ company, open, onClose }) => {
  const [tab, setTab] = useState(0);

  if (!company) return null;

  const tabStyle = {
    textTransform: 'none',
    fontWeight: 700,
    minHeight: 38,
    px: 1.5,
    borderRadius: 2,
    '&.Mui-selected': {
      color: '#253064',
      bgcolor: '#eef2ff',
    },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 620 }, maxWidth: '100%' } }}
    >
      <Box
        sx={{
          p: 2.25,
          color: '#fff',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #4B5AB6 52%, #6d7ad6 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 42, height: 42 }}>
              <DomainOutlinedIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{company.companyName}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.92 }}>{company.industry} • {company.location}</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" label={company.size} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }} />
          <Chip size="small" label={`${company.activeOpportunities} Active Opps`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }} />
          <Chip size="small" label={`$${company.revenue.toLocaleString()} Revenue`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }} />
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{ borderBottom: '1px solid #e2e8f0', px: 1, py: 0.75, '& .MuiTabs-flexContainer': { gap: 0.75 } }}
      >
        <Tab label="Overview" sx={tabStyle} />
        <Tab label="Contacts" sx={tabStyle} />
        <Tab label="Opportunities" sx={tabStyle} />
        <Tab label="Quotes" sx={tabStyle} />
        <Tab label="Activities" sx={tabStyle} />
        <Tab label="Files" sx={tabStyle} />
        <Tab label="Notes" sx={tabStyle} />
      </Tabs>

      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1, bgcolor: '#f8fafc' }}>
        {tab === 0 && (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
              <Typography variant="subtitle2" color="text.secondary">Company Overview</Typography>
              <Typography sx={{ mt: 1 }}><strong>Industry:</strong> {company.industry}</Typography>
              <Typography><strong>Size:</strong> {company.size}</Typography>
              <Typography><strong>Website:</strong> {company.website}</Typography>
              <Typography><strong>Revenue:</strong> ${company.revenue.toLocaleString()}</Typography>
              <Typography><strong>Active Opportunities:</strong> {company.activeOpportunities}</Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Primary Contact</Typography>
              <Stack spacing={0.75}>
                <Typography sx={{ fontWeight: 700 }}>{company.contactPerson}</Typography>
                <Typography variant="body2">{company.email}</Typography>
                <Typography variant="body2">{company.phone}</Typography>
              </Stack>
            </Paper>
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={1}>
            {company.contacts.map((contact, index) => (
              <Paper key={`${contact.email}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 700 }}>{contact.name}</Typography>
                <Typography variant="body2" color="text.secondary">{contact.role}</Typography>
                <Typography variant="body2">{contact.email}</Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 2 && (
          <Stack spacing={1}>
            {company.opportunities.map((opp, index) => (
              <Paper key={`${opp.name}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 700 }}>{opp.name}</Typography>
                <Typography variant="body2">Stage: <strong>{opp.stage}</strong></Typography>
                <Typography variant="body2">Value: <strong>${opp.value.toLocaleString()}</strong></Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 3 && (
          <Stack spacing={1}>
            {company.quotes.map((quote, index) => (
              <Paper key={`${quote.number}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 700 }}>{quote.number}</Typography>
                <Typography variant="body2">Status: <strong>{quote.status}</strong></Typography>
                <Typography variant="body2">Amount: <strong>${quote.amount.toLocaleString()}</strong></Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 4 && (
          <Stack spacing={1}>
            {company.activities.map((activity, index) => (
              <Paper key={`${activity.date}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
                <Typography variant="caption" color="text.secondary">{activity.date}</Typography>
                <Typography>{activity.text}</Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 5 && (
          <Stack spacing={1}>
            {company.files.map((file, index) => (
              <Paper key={`${file}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
                <Typography>{file}</Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 6 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#fff' }}>
            <Typography>{company.notes || 'No notes available.'}</Typography>
          </Paper>
        )}
      </Box>
    </Drawer>
  );
};

export const Companies = () => {
  const [companies, setCompanies] = useState(companiesSeed);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [openNewCompany, setOpenNewCompany] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newCompany, setNewCompany] = useState({
    companyName: '',
    industry: '',
    contactPerson: '',
    phone: '',
    email: '',
    revenue: '',
  });

  const industries = useMemo(
    () => ['all', ...Array.from(new Set(companies.map(c => c.industry))).sort((a, b) => a.localeCompare(b))],
    [companies]
  );

  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return companies.filter(company => {
      const matchesSearch =
        !term ||
        [company.companyName, company.contactPerson, company.phone, company.email]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(term));
      const matchesIndustry = industryFilter === 'all' || company.industry === industryFilter;
      return matchesSearch && matchesIndustry;
    });
  }, [companies, searchTerm, industryFilter]);

  const handleCreateCompany = () => {
    if (!newCompany.companyName || !newCompany.industry || !newCompany.contactPerson) {
      setSnackbar({ open: true, message: 'Please fill required fields', severity: 'error' });
      return;
    }

    const company = {
      id: `CMP-${Date.now()}`,
      companyName: newCompany.companyName,
      industry: newCompany.industry,
      contactPerson: newCompany.contactPerson,
      phone: newCompany.phone || '-',
      email: newCompany.email || '-',
      activeOpportunities: 0,
      revenue: Number(newCompany.revenue) || 0,
      website: '-',
      size: 'SMB',
      location: '-',
      notes: '',
      contacts: [],
      opportunities: [],
      quotes: [],
      activities: [],
      files: [],
    };

    setCompanies(prev => [company, ...prev]);
    setOpenNewCompany(false);
    setNewCompany({ companyName: '', industry: '', contactPerson: '', phone: '', email: '', revenue: '' });
    setSnackbar({ open: true, message: 'Company created successfully', severity: 'success' });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <BusinessIcon sx={{ color: '#4B5AB6', fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#253064' }}>
                Companies
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Enterprise account directory and profile workspace
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2.5 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setOpenNewCompany(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#4B5AB6',
              '&:hover': { bgcolor: '#3b4898' },
            }}
          >
            New Company
          </Button>

          <TextField
            size="small"
            placeholder="Search company, contact, phone, email"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            sx={{ flex: '1 1 280px', maxWidth: 420, bgcolor: '#fff' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180, bgcolor: '#fff' }}>
            <InputLabel>Filter Industry</InputLabel>
            <Select value={industryFilter} label="Filter Industry" onChange={event => setIndustryFilter(event.target.value)}>
              {industries.map(industry => (
                <MenuItem key={industry} value={industry}>
                  {industry === 'all' ? 'All Industries' : industry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip
            icon={<FilterListIcon />}
            label={`${filteredCompanies.length} result${filteredCompanies.length > 1 ? 's' : ''}`}
            sx={{ fontWeight: 700, bgcolor: '#eef2ff', color: '#4B5AB6', border: '1px solid #c7d2fe' }}
          />
        </Box>

        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Company Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Industry</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contact Person</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Active Opportunities</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Revenue</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#64748b' }}>
                    No companies found for the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map(company => (
                  <TableRow key={company.id} hover>
                    <TableCell sx={{ fontWeight: 700, color: '#253064' }}>{company.companyName}</TableCell>
                    <TableCell>{company.industry}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PersonOutlineIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        {company.contactPerson}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LocalPhoneOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        {company.phone}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <EmailOutlinedIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        {company.email}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={company.activeOpportunities} size="small" sx={{ fontWeight: 700, bgcolor: '#eef2ff', color: '#4B5AB6' }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <MonetizationOnOutlinedIcon sx={{ fontSize: 16, color: '#15803d' }} />
                        ${company.revenue.toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => setSelectedCompany(company)}>
                        <VisibilityIcon sx={{ color: '#4B5AB6' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CompanyProfileDrawer
        company={selectedCompany}
        open={Boolean(selectedCompany)}
        onClose={() => setSelectedCompany(null)}
      />

      <Dialog
        open={openNewCompany}
        onClose={() => setOpenNewCompany(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: '#fff',
            py: 2,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #4B5AB6 65%, #6d7ad6 100%)',
          }}
        >
          New Company
          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
            Add a company profile to your CRM workspace
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f8fafc' }}>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <TextField
              label="Company Name *"
              value={newCompany.companyName}
              onChange={event => setNewCompany(prev => ({ ...prev, companyName: event.target.value }))}
              fullWidth
              sx={{ bgcolor: '#fff' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Industry *"
              value={newCompany.industry}
              onChange={event => setNewCompany(prev => ({ ...prev, industry: event.target.value }))}
              fullWidth
              sx={{ bgcolor: '#fff' }}
            />
            <TextField
              label="Contact Person *"
              value={newCompany.contactPerson}
              onChange={event => setNewCompany(prev => ({ ...prev, contactPerson: event.target.value }))}
              fullWidth
              sx={{ bgcolor: '#fff' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                label="Phone"
                value={newCompany.phone}
                onChange={event => setNewCompany(prev => ({ ...prev, phone: event.target.value }))}
                fullWidth
                sx={{ bgcolor: '#fff' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalPhoneOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Email"
                value={newCompany.email}
                onChange={event => setNewCompany(prev => ({ ...prev, email: event.target.value }))}
                fullWidth
                sx={{ bgcolor: '#fff' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TextField
              label="Revenue"
              type="number"
              value={newCompany.revenue}
              onChange={event => setNewCompany(prev => ({ ...prev, revenue: event.target.value }))}
              fullWidth
              sx={{ bgcolor: '#fff' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MonetizationOnOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff' }}>
          <Button onClick={() => setOpenNewCompany(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
          <Button
            onClick={handleCreateCompany}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#4B5AB6', '&:hover': { bgcolor: '#3b4898' } }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2200}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;
